import { render as toPlainText } from 'datocms-structured-text-to-plain-text'
import { type ImageObject, type Organization, type Person } from 'schema-dts'

import { getAbsoluteUrl } from '@/internal/utils/generate-url'

import { generateUrlFromNode } from './url'

type Meta = {
  firstPublishedAt?: string | null
  publishedAtTimestamp?: string | null
}

export const publisherId = {
  '@id': 'https://insurance.nttdata.com/#publisher'
}

export const getPublishedDate = (meta: Meta): string =>
  meta?.firstPublishedAt ?? ''

export const getModifiedDate = (
  meta: Meta & { publishedAtTimestamp?: string | null }
): string => meta?.publishedAtTimestamp ?? meta?.firstPublishedAt ?? ''

/**
 * Converts an array of authors into an array of Person objects with microdata format.
 * @param authors - The array of authors.
 * @param siteUrl - The URL of the site.
 * @returns An array of Person objects with microdata format.
 */
export const personObjectFromAuthor = (
  authors: readonly (Queries.AuthorFragment | null)[] | null,
  siteUrl: string
): Person[] | undefined => {
  return authors?.reduce((acc: Person[], author) => {
    if (author?.person) {
      const microdataAuthor = personObject(author.person, siteUrl)
      acc.push(microdataAuthor)
    }
    return acc
  }, [])
}

/**
 * Converts an array of authors into an array of Person objects with microdata format.
 * @param authors - The array of authors.
 * @param siteUrl - The URL of the site.
 * @returns An array of Person objects with microdata format.
 */
export const OrganizationObject = (
  person: Queries.PersonDetailFragment
): Organization => {
  const { company } = person
  const organization: Organization = { '@type': 'Organization' }
  organization.name = company?.externalName ?? 'NTT DATA'
  organization.url = company?.url ?? undefined
  organization.logo = company?.logo?.url ?? undefined
  return organization
}

/**
 * Converts a person into a Person object with microdata format.
 * @param person - The person.
 * @param siteUrl - The URL of the site.
 * @returns A Person object with microdata format.
 */
export const personObject = (
  person: Queries.PersonDetailFragment,
  siteUrl: string
): Person => {
  const { firstName, secondName, bio, job, photo } = person
  const image: ImageObject = {
    '@type': 'ImageObject',
    url: photo?.url ?? undefined
  }
  const url = generateUrlFromNode(person)
  return {
    '@type': 'Person',
    name: `${firstName} ${secondName}`,
    description: bio ?? undefined,
    jobTitle: job ?? undefined,
    image,
    worksFor: OrganizationObject(person),
    url: getAbsoluteUrl(siteUrl, url)
  }
}

export const logo: ImageObject = {
  '@type': 'ImageObject',
  url: 'https://insurance.nttdata.com/media/header_logo.svg',
  width: '160',
  height: '40',
  creditText: 'NTT DATA'
}

export const personObjectFromSpeaker = (
  speakers:
    | readonly ({ person: Queries.PersonDetailFragment | null } | null)[]
    | null,
  siteUrl: string
): Person[] | undefined =>
  speakers?.reduce((acc: Person[], speaker) => {
    if (speaker?.person) {
      acc.push(personObject(speaker.person, siteUrl))
    }
    return acc
  }, [])

export const keywordsFromTags = (
  tags: readonly ({ externalName?: string | null } | null)[] | null | undefined
): string | undefined =>
  tags
    ?.map((tag) => tag?.externalName)
    .filter(Boolean)
    .join(', ') || undefined

export const getImageUrl = (
  image?: { url?: string | null; gatsbyImageData?: unknown } | null
): string | undefined => {
  if (image == null) return undefined
  if ('url' in image && typeof image.url === 'string') {
    return image.url
  }
  try {
    const gatsbyImage = image.gatsbyImageData as {
      images?: { fallback?: { src?: string } }
    }
    return gatsbyImage?.images?.fallback?.src
  } catch {
    return undefined
  }
}

export const structuredTextToPlain = (
  content?: { value?: unknown } | null
): string | undefined => {
  try {
    if (content && 'value' in content && content.value) {
      const result = toPlainText(
        content.value as Parameters<typeof toPlainText>[0]
      )
      return result || undefined
    }
    return undefined
  } catch {
    return undefined
  }
}

export const transcriptToText = (
  transcription?: readonly ({ text?: string | null } | null)[] | null
): string | undefined => {
  if (!Array.isArray(transcription)) return undefined
  const text = transcription
    .map((t) => (typeof t?.text === 'string' ? t.text : ''))
    .filter(Boolean)
    .join(' ')
  return text || undefined
}

export const extractArticleBody = (
  data: Record<string, unknown>,
  description?: string
): string | undefined => {
  const candidates: (string | undefined)[] = [
    structuredTextToPlain(data.content as { value?: unknown } | null),
    structuredTextToPlain(data.intro as { value?: unknown } | null),
    structuredTextToPlain(data.clientNeed as { value?: unknown } | null),
    structuredTextToPlain(data.howDid as { value?: unknown } | null),
    structuredTextToPlain(data.result as { value?: unknown } | null),
    transcriptToText(
      data.transcription as readonly ({ text?: string | null } | null)[] | null
    ),
    typeof data.description === 'string' ? data.description : undefined
  ]
  for (const candidate of candidates) {
    if (!candidate) continue
    const trimmed = candidate.trim()
    if (!trimmed) continue
    if (
      description &&
      trimmed.length <= description.trim().length &&
      trimmed.startsWith(description.trim())
    ) {
      continue
    }
    return trimmed
  }
  return undefined
}
